import { useCanvasLoop } from "@/4-components/animations/useCanvasLoop";
import { makeGlobeDraw } from "@/4-components/composites/globe";
import { GREEN, GREEN_LIGHT } from "@/5-shared/constants/palette";
import { rgba } from "@/5-shared/utils/color";

/**
 * The support layer behind a bento mini: a low-alpha green canvas that gives the tile
 * some depth without competing with the signature animation in front of it. Inspired by
 * the five `<canvas>` renderers in `docs`-adjacent explorations, rising lines, a
 * wireframe globe, and three bespoke fields tuned to each proposition.
 *
 * Raw canvas + `requestAnimationFrame`, the same idiom as `heroScene.ts` and
 * `EnergyField` (anime.js stays the site's only animation *library*; this is manual
 * rendering). `useCanvasLoop` owns the lifecycle: the loop only runs while the tile
 * is on screen, honours `prefers-reduced-motion` with a single held frame, and tears
 * everything down on unmount.
 */

export type Variant = "ascend" | "globe" | "descend" | "contours" | "calm";

type Draw = (ctx: CanvasRenderingContext2D, w: number, h: number, t: number) => void;

/* --- ascend: sparse lines drifting up-right (EBITDA, growth) --- */
const ASCEND_LINES = 8;

const drawAscend: Draw = (ctx, w, h, t) => {
  const time = t * 0.0025;
  ctx.lineCap = "round";
  for (let i = 0; i < ASCEND_LINES; i++) {
    const p = i / ASCEND_LINES;
    const shift = (time * (0.35 + p * 0.15) + p * 1.4) % 1.4;
    const startX = -w * 0.3 + shift * w;
    const startY = h * 1.15 - shift * h;

    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(startX + w * 0.45, startY - h * 0.3);
    ctx.strokeStyle = rgba(i % 3 === 0 ? GREEN_LIGHT : GREEN, 0.035 + p * 0.045);
    ctx.lineWidth = 1 + p * 0.6;
    ctx.stroke();
  }
};

/* --- globe: slowly spinning wireframe sphere in the corner (Impatto, reach) --- */
const drawGlobe: Draw = makeGlobeDraw({ center: [0.95, 0.78], radius: 0.9, tilt: 0.35, rotY: (t) => t * 0.004 });

/* --- descend: diagonal streaks drifting downward (Riduzione, cost coming down) --- */
const DESCEND_LINES = 12;

const drawDescend: Draw = (ctx, w, h, t) => {
  const time = t * 0.004;
  for (let i = 0; i < DESCEND_LINES; i++) {
    const p = i / DESCEND_LINES;
    // each streak slides down-right and wraps around, offset by its index
    const shift = (time * (0.4 + p * 0.5) + p) % 1.4;
    const startX = -w * 0.3 + shift * w * 1.1;
    const startY = -h * 0.4 + shift * h * 1.3;
    const alpha = 0.05 + Math.sin(p * Math.PI) * 0.08;

    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(startX + w * 0.5, startY + h * 0.7);
    ctx.strokeStyle = rgba(i % 3 === 0 ? GREEN_LIGHT : GREEN, alpha);
    ctx.lineWidth = 1.2 + p * 1.4;
    ctx.lineCap = "round";
    ctx.stroke();
  }
};

/* --- contours: horizontal lines drifting up, tightening (Conformità, the standard) --- */
const CONTOUR_LINES = 9;
const CONTOUR_STEPS = 40;

const drawContours: Draw = (ctx, w, h, t) => {
  const time = t * 0.003;
  ctx.lineCap = "round";
  for (let i = 0; i < CONTOUR_LINES; i++) {
    const p = i / (CONTOUR_LINES - 1);
    // lines pack tighter toward the top: baseline eased upward
    const baseY = h * (1 - Math.pow(p, 1.4)) * 0.95 + h * 0.02;
    const drift = ((time * 0.5 + p) % 1) * 6 - 3;
    const amp = (6 + p * 10) * (1 - p * 0.4);

    ctx.beginPath();
    for (let s = 0; s <= CONTOUR_STEPS; s++) {
      const f = s / CONTOUR_STEPS;
      const x = f * w;
      const y = baseY + drift + Math.sin(f * 4 + time + p * 3) * amp + Math.sin(f * 7 + time * 0.6) * amp * 0.35;
      if (s === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = rgba(p > 0.6 ? GREEN : GREEN_LIGHT, 0.05 + p * 0.1);
    ctx.lineWidth = 1 + p * 1.2;
    ctx.stroke();
  }
};

/* --- calm: low-amplitude damped sine field (Copertura, reduced volatility) --- */
const CALM_LINES = 7;
const CALM_STEPS = 48;

const drawCalm: Draw = (ctx, w, h, t) => {
  const time = t * 0.004;
  ctx.lineCap = "round";
  for (let i = 0; i < CALM_LINES; i++) {
    const p = i / (CALM_LINES - 1);
    const baseY = h * (0.25 + p * 0.55);
    // amplitude decays left→right: the oscillation settles into a flat line
    ctx.beginPath();
    for (let s = 0; s <= CALM_STEPS; s++) {
      const f = s / CALM_STEPS;
      const damp = Math.pow(1 - f, 1.6);
      const amp = (10 + p * 8) * damp;
      const x = f * w;
      const y = baseY + Math.sin(f * 6 - time * (1 + p * 0.4) + p * 2) * amp;
      if (s === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = rgba(i % 2 === 0 ? GREEN : GREEN_LIGHT, 0.05 + (1 - p) * 0.08);
    ctx.lineWidth = 1 + (1 - p) * 1.2;
    ctx.stroke();
  }
};

const DRAW: Record<Variant, Draw> = {
  ascend: drawAscend,
  globe: drawGlobe,
  descend: drawDescend,
  contours: drawContours,
  calm: drawCalm,
};

/** A representative frame for reduced-motion, mid-cycle, so the field looks alive but still. */
const STILL_FRAME = 2600;

export function CanvasBackdrop({ variant }: { variant: Variant }) {
  // dpr capped at 2, five of these can share a viewport, retina-3 backing stores aren't worth it
  const ref = useCanvasLoop(DRAW[variant], { still: STILL_FRAME, maxDpr: 2 }, [variant]);

  // recede on hover so the foreground scene reads clearly, the backdrop is support, not subject
  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full opacity-70 transition-opacity duration-500 group-hover:opacity-30"
    />
  );
}
